import type {
  InboundMessage,
  OutboundMessage,
  BridgeEnvelope,
} from '@/types/bridge';
import { BRIDGE_SOURCE } from '@/types/bridge';

type InboundHandler<T extends InboundMessage['type']> = (
  payload: Extract<InboundMessage, { type: T }> extends { payload: infer P } ? P : void,
) => void;

type HandlerMap = {
  [K in InboundMessage['type']]?: Set<InboundHandler<K>>;
};

/**
 * PostMessage 双向通讯桥梁（单例）
 *
 * - 监听父页面发来的 InboundMessage
 * - 向父页面发送 OutboundMessage
 * - 自动过滤非 chat-bi 来源的消息
 * - 支持指定 targetOrigin 增强安全性
 */
class PostMessageBridge {
  private handlers: HandlerMap = {};
  private listening = false;
  private targetOrigin: string = '*';

  /** 设置 targetOrigin（生产环境建议指定为宿主域名） */
  setTargetOrigin(origin: string): void {
    this.targetOrigin = origin;
  }

  /** 开始监听父页面消息 */
  listen(): void {
    if (this.listening) return;
    this.listening = true;
    window.addEventListener('message', this.handleMessage);
  }

  /** 停止监听 */
  unlisten(): void {
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener('message', this.handleMessage);
  }

  /** 注册指定类型消息的处理器，返回取消注册函数 */
  on<T extends InboundMessage['type']>(type: T, handler: InboundHandler<T>): () => void {
    if (!this.handlers[type]) {
      (this.handlers as Record<string, Set<unknown>>)[type] = new Set();
    }
    const set = this.handlers[type] as Set<unknown>;
    set.add(handler);

    return () => {
      set.delete(handler);
    };
  }

  /** 向父页面发送消息 */
  send(message: OutboundMessage): void {
    if (!window.parent || window.parent === window) return;

    const envelope: BridgeEnvelope<OutboundMessage> = {
      source: BRIDGE_SOURCE,
      data: message,
    };
    window.parent.postMessage(envelope, this.targetOrigin);
  }

  /** 内部消息处理器 */
  private handleMessage = (event: MessageEvent): void => {
    // console.log('[ChatBI Bridge] 收到 message 事件, origin:', event.origin, 'data:', event.data);

    const envelope = event.data as BridgeEnvelope<InboundMessage> | undefined;
    if (!envelope || envelope.source !== BRIDGE_SOURCE) {
      // console.log('[ChatBI Bridge] 已忽略: source 不匹配, 期望:', BRIDGE_SOURCE, '实际:', envelope?.source);
      return;
    }

    const { data } = envelope;
    if (!data || !data.type) {
      // console.warn('[ChatBI Bridge] 缺少 data 或 data.type, envelope:', envelope);
      return;
    }

    const handlerSet = this.handlers[data.type];
    if (!handlerSet || handlerSet.size === 0) {
      // console.warn('[ChatBI Bridge] 没有找到类型的处理器:', data.type, '已注册的类型:', Object.keys(this.handlers));
      return;
    }

    const payload = 'payload' in data ? data.payload : undefined;
    // console.log('[ChatBI Bridge] 分发消息, type:', data.type, 'payload:', payload);
    handlerSet.forEach((handler) => {
      try {
        (handler as (p: unknown) => void)(payload);
      } catch (err) {
        console.error(`[ChatBI Bridge] Error handling "${data.type}":`, err);
      }
    });
  };

  /** 销毁实例，移除所有监听 */
  destroy(): void {
    this.unlisten();
    this.handlers = {};
  }
}

export const bridge = new PostMessageBridge();
